import { useAppConfiguration } from "@/api/app-configuration.api";
import { useStopLists } from "@/api/stop-list.api";
import { useTrialPeriod } from "@/hooks/use-trial-period";
import { getCampaignPriority } from "@/services/configurations.service";
import { formatScheduleLabel } from "@/services/schedule.service";
import type {
  Configuration,
  ContactConfiguration,
  CountsResponse,
  NewCampaign,
} from "@/types/campaign";
import { SimpleUser } from "@/types/user";
import {
  getLocalTimeZone,
  isToday,
  parseAbsolute,
  parseDate,
} from "@internationalized/date";
import { linkOptions, ToOptions } from "@tanstack/react-router";
import { useMemo } from "react";
import { detectCurrentStrategy } from "./configurations";
import { formatNumber } from "./number-utils";

export function formatListValues(
  values: string[],
  lastSeparator = "og",
  withQuotes = true,
) {
  if (values.length === 0) return "";
  if (values.length === 1)
    return `${withQuotes ? '"' : ""}${values[0]}${withQuotes ? '"' : ""}`;
  if (values.length === 2)
    return `${withQuotes ? '"' : ""}${values[0]}${withQuotes ? '"' : ""} ${lastSeparator} ${withQuotes ? '"' : ""}${values[1]}${withQuotes ? '"' : ""}`;

  return `${withQuotes ? '"' : ""}${values[0]}${withQuotes ? '"' : ""} ${lastSeparator} ${values.length - 1} andre`;
}

export function formatPeriodDescription(
  startDate?: string | null,
  endDate?: string | null,
  estimatedEndDate?: string,
) {
  if (!startDate && !endDate) {
    return "Konfigurer periode";
  }

  if (startDate && !endDate) {
    const start = new Date(startDate);
    if (estimatedEndDate) {
      const end = new Date(estimatedEndDate);
      const parsedStart = startDate.includes("T")
        ? parseAbsolute(
            startDate.endsWith("Z") ? startDate : startDate + "Z",
            "UTC",
          )
        : parseDate(startDate); // Need to support both formats
      const isStartToday = isToday(parsedStart, getLocalTimeZone());
      // Get duration in months/weeks/days depending on length
      const durationInDays = Math.floor(
        (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
      );
      if (durationInDays <= 0) {
        return "-";
      }
      if (durationInDays < 7) {
        return `Starter ${
          isStartToday ? "i dag" : "d. " + start.toLocaleDateString("da-DK")
        } og kan levere leads i ${durationInDays} dag${
          durationInDays > 1 ? "e" : ""
        }`;
      } else if (durationInDays >= 7 && durationInDays < 30) {
        const weeks = Math.floor(durationInDays / 7);
        return `Starter ${
          isStartToday ? "i dag" : "d. " + start.toLocaleDateString("da-DK")
        } og kan levere leads i ${weeks} uge${weeks > 1 ? "r" : ""}`;
      } else if (durationInDays >= 30 && durationInDays <= 180) {
        const months = Math.floor(durationInDays / 30);
        return `Starter ${
          isStartToday ? "i dag" : "d. " + start.toLocaleDateString("da-DK")
        } og kan levere leads i ${months} måned${months > 1 ? "er" : ""}`;
      }

      return `Starter ${
        isStartToday ? "i dag" : "d. " + start.toLocaleDateString("da-DK")
      } og er estimeret til at løbe tør for leads omkring d. ${end.toLocaleDateString(
        "da-DK",
      )}`;
    }
  }

  if (!startDate && endDate) {
    const end = new Date(endDate);
    return `Slutter d. ${end.toLocaleDateString("da-DK")}`;
  }

  if (startDate && endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const weeks = Math.floor(
      (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 7),
    );
    return `${start.toLocaleDateString("da-DK")} - ${end.toLocaleDateString(
      "da-DK",
    )} (${weeks < 1 ? "< 1 uge" : weeks > 1 ? `${weeks} uger` : "1 uge"})`;
  }

  return "Ingen datoer sat";
}

export function formatCampaignPriority(campaign: NewCampaign) {
  return `${detectCurrentStrategy(campaign?.configurations).title}`;
}

export function useTriggersDescription(campaign?: NewCampaign) {
  const { data: appConfiguration } = useAppConfiguration();
  return campaign?.triggers
    ?.map(
      (trigger) =>
        appConfiguration?.triggerTypes.find((t) => t.event === trigger.event)
          ?.label,
    )
    .join(", ");
}

export function formatTriggerDeliveryDescription(campaign?: NewCampaign) {
  if (!campaign?.maxLeadsPerWeek) return "Tilføj";

  return `Op til ${campaign.maxLeadsPerWeek} leads per uge`;
}

export function formatConfigurationsDescription(
  configurations: Configuration[],
  configurationCounts: CountsResponse[],
) {
  const enabledConfigurations = configurations.filter(
    (config) => config.enabled !== false && !config.deleted,
  );

  if (!enabledConfigurations.length) return "Vælg målgruppe";

  const countsByQuality = new Map(
    configurationCounts.map(({ quality, count }) => [quality, count]),
  );

  const countsText = enabledConfigurations
    .map(({ quality, name }) =>
      name
        ? `${formatNumber(
            countsByQuality.get(quality) ?? 0,
          )} ${name?.toLowerCase()}`
        : null,
    )
    .filter((c) => c !== null)
    .join(", ");

  return `${countsText}`;
}
